import type { UserInfo, UserConfig } from '/#/store'

import { defineStore } from 'pinia'
import { store } from '/@/store'
import { RoleEnum } from '/@/enums/roleEnum'
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY } from '/@/enums/cacheEnum'
import { getAuthCache, setAuthCache } from '/@/utils/auth'
import { getUserInfo } from '/@/api/sys/user'
import { getIsStationUser } from '/@/api/userInfo/stationUser'
import { useI18n } from '/@/hooks/web/useI18n'
import { useMessage } from '/@/hooks/web/useMessage'
import { h } from 'vue'
import { useGlobSetting } from '/@/hooks/setting'
import { openWindow } from '/@/neolix-utils/src/index'
import { router } from '/@/router/index'
// import { router } from '/@/router'
const globSetting = useGlobSetting()
interface UserState {
  userInfo: Nullable<UserInfo>
  token?: string
  roleList: RoleEnum[]
  sessionTimeout?: boolean
  lastUpdateTime: number
  homePath?: string
  allRoutes: []
  menuRoutes: []
  permissionEnum: []
  permissionCodeArr: string[]
  userConfig: UserConfig
  unlock: any
  dpList: Recordable[]
  supervisor: boolean
  businessTypeList: number[]
  /** 服务站用户：由 /api/user-info/is-station-user 在登录后拉取 */
  isStationUser: boolean
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: null,
    // token
    token: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    // 用户路由权限首页
    homePath: undefined,
    // 用户能访问的所有路由
    allRoutes: [],
    // 菜单中要显示的路由，从allRouter的hideMenu属性过滤
    menuRoutes: [],
    // 用户有的操作权限
    permissionEnum: [],
    // 统一权限标识数组(菜单+按钮)
    permissionCodeArr: [],
    // 用户表单配置
    userConfig: {},
    //解锁
    unlock: null,
    dpList: [],
    supervisor: false,
    businessTypeList: [],
    isStationUser: false,
  }),
  getters: {
    getUserInfo(): UserInfo {
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {}
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY)
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY)
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime
    },
    getHomePath(): string | undefined {
      return this.homePath
    },
    getAllRoutes(): [] {
      return this.allRoutes || []
    },
    getMenuRoutes(): [] {
      return this.menuRoutes || []
    },
    getPermissionEnum(): string[] {
      return this.permissionEnum || []
    },
    getPermissionCodeArr(): string[] {
      return this.permissionCodeArr || []
    },
    getUserConfig(): UserConfig {
      return this.userConfig || {}
    },
    getUnlock(): any {
      return this.unlock
    },
    getDpList(): Recordable[] {
      return this.dpList
    },
    getBusinessTypeList(): number[] {
      return this.businessTypeList
    },
    getIsStationUser(): boolean {
      return this.isStationUser
    },
  },
  actions: {
    setUnlock(unlock) {
      this.unlock = unlock
    },
    setToken(info: string | undefined) {
      this.token = info ? info : '' // for null or undefined value
      setAuthCache(TOKEN_KEY, info)
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList
      setAuthCache(ROLES_KEY, roleList)
    },
    setUserInfo(info: UserInfo | null) {
      this.userInfo = info
      this.lastUpdateTime = new Date().getTime()
      this.dpList = info?.dpList || []
      this.businessTypeList = info?.businessTypeList || []
      if (!info) {
        this.isStationUser = false
      }
      setAuthCache(USER_INFO_KEY, info)
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag
    },
    setHomePath(path: string) {
      this.homePath = path
    },
    setDpList(dpList: Recordable[]) {
      this.dpList = dpList
    },
    resetState() {
      this.userInfo = null
      this.token = ''
      this.roleList = []
      this.sessionTimeout = false
      this.isStationUser = false
    },
    async getUserInfoAction(params?: { userId: number; companyId: number }) {
      if (!this.getToken) return null
      try {
        const userInfo = await getUserInfo(params)
        this.setUserInfo(userInfo)
        this.setAuth(userInfo.menuJsonArray[0])
        await this.refreshIsStationUserAction(userInfo)
        return userInfo
      } catch (error: any) {
        router.push({
          path: '/permission-exception',
          query: { code: error.code, type: 'userInfo' },
        })
        throw error
      }
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false, redirectUrl = location.href) {
      this.setToken(undefined)
      this.setSessionTimeout(false)
      this.setUserInfo(null)
      if (goLogin) {
        // 判断是否启用SSO登录
        const isUseSSO = globSetting.loginTypeSSO === 'true'

        if (isUseSSO) {
          // SSO登出逻辑
          const appId = globSetting.appId
          // 登出后回跳地址为登录，登录后回跳当前页面
          // 如果url上有token参数，需要处理掉，避免pass获取到过期的token
          const tokenReg = /[&]?token=[^&#?]+/g
          redirectUrl = redirectUrl.replace(tokenReg, '')

          const passportUrl = globSetting.passportUrl
          const loginUrl =
            `${passportUrl}?response_type=token&client_id=${appId}&redirect_uri=` +
            encodeURIComponent(redirectUrl)
          const openUrl = `${passportUrl}/logout?redirect=` + encodeURIComponent(loginUrl)
          const unlock = this.getUnlock
          try {
            if (unlock) {
              await unlock()
            }
          } catch (e) {}

          openWindow(openUrl, { target: '_self' })
        } else {
          // 跳转到本地登录页面
          window.location.href = '/'
        }
      }
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage()
      const { t } = useI18n()
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          await this.logout(true)
        },
      })
    },
    setAllRoutes(routes) {
      this.allRoutes = routes || []
    },
    setPermissionEnum(permissionEnum) {
      this.permissionEnum = permissionEnum || []
    },
    setPermissionCodeArr(permissionCodeArr: string[]) {
      this.permissionCodeArr = permissionCodeArr || []
    },
    /**
     * @description: 处理权限数据,提取所有权限标识
     */
    setAuth(resAuth: any) {
      const menuJson = resAuth?.menuJson

      if (!menuJson) {
        this.setPermissionCodeArr([])
        return
      }

      // 递归提取所有权限标识(type=0菜单 + type=1按钮)，固定走 name(menuName) 模式
      const permissionArr: string[] = []
      const recursion = (data: any[]) => {
        data.forEach((v) => {
          const authName = v?.menuName || v?.name
          if (v.check && authName) {
            permissionArr.push(authName)
          }
          if (v.children) {
            recursion(v.children)
          }
        })
      }
      recursion(menuJson)

      this.setPermissionCodeArr(permissionArr)
      console.log(permissionArr)
    },
    /**
     * @description: 拉取权限信息(用于路由过滤)
     */
    async getPermissionRoutesAction() {
      try {
        const menuPermissions = this.permissionCodeArr || []
        return menuPermissions
      } catch (error: any) {
        return []
      }
    },

    setUserConfig(userConfig: UserConfig) {
      this.userConfig = userConfig || {}
    },

    /** 登录后调用：是否服务站用户，用于屏蔽部分菜单 */
    async refreshIsStationUserAction(userInfo?: UserInfo | null) {
      const info = userInfo ?? this.getUserInfo
      const uid = info?.userId
      if (uid == null || uid === '') {
        this.isStationUser = false
        return
      }
      try {
        const flag = await getIsStationUser(uid)
        this.isStationUser = flag === true
      } catch {
        this.isStationUser = false
      }
    },
  },
})

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store)
}
